import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { BRAND_DARKER } from './healthWalletConstants';
import type { NoteItem } from './healthWalletSlice';
// Rendering + sanitising a stored clinical document now lives in ONE shared component, used both by this
// feed (narrative INLINE) and by the procedures drill-down (ids only → fetched on expand). See DocumentBlock.tsx.
import { DocumentBlock, isBoilerplate, fmtDate } from './DocumentBlock';

// Thin oversized chevron for the notes navigator (Leo: the MUI glyph reads too THICK at this
// size) — border-drawn angle, so the stroke stays constant however large the arms get. Uses
// currentColor → inherits the IconButton's grey + hover darken + disabled opacity.
const ThinChevron: FC<{ dir: 'left' | 'right' }> = ({ dir }) => (
  <Box
    sx={{
      width: { xs: 44, sm: 58 },
      height: { xs: 44, sm: 58 },
      borderTop: '6px solid currentColor',
      borderRight: '6px solid currentColor',
      transform: dir === 'right' ? 'translateX(-7px) rotate(45deg)' : 'translateX(7px) rotate(-135deg)',
    }}
  />
);


// One note, rendered IN FULL (no expand/collapse — you read by scrolling). Composition/text-DocumentReference
// narrative is inline (from get_patient_notes_page); binary (PDF/image) shows a Download button that fetches the
// base64 lazily ON CLICK (get_patient_note_content) so we never eagerly pull heavy blobs.
// memo: the note-linked nav recomputes on every scroll frame → without this, NoteBlock would re-render
// (and re-run DOMPurify over the whole narrative) each frame. note is stable while scrolling → memo skips.
const NoteBlock = memo(({ note }: { note: NoteItem }) => (
  <DocumentBlock
    id={note.id}
    title={note.title || note.type || (note.kind === 'composition' ? 'Clinical Note' : 'Document')}
    subtitle={[note.type, fmtDate(note.date)].filter(Boolean).join(' · ')}
    narrative={note.narrative}
    hasBinary={note.hasBinary}
  />
));

/**
 * "Notes" tab — clinical notes from get_patient_notes_page, ONE NOTE AT A TIME (Leo 2026-07-20).
 * Clean axis separation: VERTICAL scroll = read the current note (it's the only thing on the page, so the
 * natural page scroll IS the note's scroll); HORIZONTAL (edge arrows / touch swipe / ←→ keys) = move to the
 * prev/next note. `>` past the last LOADED note pulls the next page then advances. Replaces the old
 * free-scroll feed + scroll-spy (vertical did double duty → confusing). project_hw_records_rendering_split.
 */
export const NotesSection: FC<{
  notes: NoteItem[];
  total: number;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}> = ({ notes, total, onLoadMore, loadingMore }) => {
  const hasMore = notes.length < total;

  // Only the notes that actually render (NoteBlock returns null for boilerplate-only rows). current indexes
  // into THIS list; the pager steps over real notes.
  const visible = useMemo(
    () => notes.filter((n) => n.hasBinary || !isBoilerplate(n.narrative)),
    [notes],
  );

  const READING_LINE = 96; // note top sits this far below the viewport top on nav → clears the fixed navbar
  const [current, setCurrent] = useState(0);
  const [slideDir, setSlideDir] = useState<'left' | 'right' | null>(null); // touch swipe → horizontal slide-in
  const pendingIdx = useRef<number | null>(null); // advance target waiting for the next page to mount
  const noteWrapRef = useRef<HTMLDivElement | null>(null);
  const navigatedRef = useRef(false); // skip the scroll-to-top on the initial mount (show hero+pills+note0)

  // The arrows are LINKED to the note (Leo): vertically centered on the note's VISIBLE slice, and HIDDEN
  // when the note isn't on screen — so they never float over the pills/hero above it. Recomputed from the
  // note's bounding rect on scroll / resize / note-change. (72/96 clear the fixed navbar / the counter pill.)
  const [navPos, setNavPos] = useState<{ top: number; visible: boolean }>({ top: 0, visible: false });
  const recomputeNav = useCallback(() => {
    const el = noteWrapRef.current;
    if (!el) { setNavPos((p) => (p.visible ? { ...p, visible: false } : p)); return; }
    const rect = el.getBoundingClientRect();
    const vTop = Math.max(rect.top, 72);
    const vBot = Math.min(rect.bottom, window.innerHeight - 96);
    setNavPos({ top: (vTop + vBot) / 2, visible: vBot - vTop > 40 });
  }, []);

  // FILTER/SEARCH change (parent swaps the whole notes set → first note's id changes) → back to note 0 + top
  // of the page. Appending a page keeps the first id, so `current` is preserved across loads.
  const firstId = visible[0]?.id;
  const prevFirstId = useRef(firstId);
  useEffect(() => {
    if (prevFirstId.current !== firstId) {
      prevFirstId.current = firstId;
      navigatedRef.current = false;
      setCurrent(0);
      window.scrollTo({ top: 0 });
    }
  }, [firstId]);

  // A page of all-boilerplate notes renders nothing → auto-pull the next page so we don't dead-end.
  useEffect(() => {
    if (visible.length === 0 && hasMore && onLoadMore && !loadingMore) onLoadMore();
  }, [visible.length, hasMore, loadingMore, onLoadMore]);

  // Navigate. dir marks a TOUCH swipe (→ horizontal slide-in); arrows/keys pass null (instant swap).
  const goTo = (i: number, dir: 'left' | 'right' | null = null) => {
    if (i < 0) return;
    if (i >= visible.length) {
      // Past the last LOADED note → pull the next page; the effect below advances once it mounts.
      if (hasMore && onLoadMore && !loadingMore) { pendingIdx.current = i; onLoadMore(); }
      return;
    }
    setSlideDir(dir);
    navigatedRef.current = true;
    setCurrent(i);
  };

  // After a page loads, advance to the note the user was heading toward.
  useEffect(() => {
    const idx = pendingIdx.current;
    if (idx !== null && idx < visible.length) {
      pendingIdx.current = null;
      navigatedRef.current = true;
      setCurrent(idx);
    }
  }, [visible.length]);

  // On note CHANGE (not the first mount / not a filter reset), snap the viewport to the new note's top so it
  // starts under the navbar and fills the screen. Instant — the horizontal slide-in plays over it.
  useEffect(() => {
    if (navigatedRef.current) noteWrapRef.current?.scrollIntoView({ block: 'start' });
    recomputeNav();
  }, [current, recomputeNav]);

  // Keep the arrows glued to the note's visible slice as the user scrolls / the window resizes.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; recomputeNav(); }); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    recomputeNav();
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [recomputeNav]);

  // Keyboard ←/→ (skipped while typing). Re-registered per render so it sees the latest `current`.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'ArrowRight') goTo(current + 1);
      else if (e.key === 'ArrowLeft') goTo(current - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Touch: a decisive HORIZONTAL swipe changes note (+ slide-in); vertical is left to the browser (page
  // scroll = reading the current note). Guards: ≥60px travel, ≥2× the vertical travel, <600ms; and a swipe
  // starting inside a horizontally-scrollable element (wide narrative tables) pans the table instead.
  const touchStart = useRef<{ x: number; y: number; t: number; pannable: boolean } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    let el = e.target as HTMLElement | null;
    let pannable = false;
    while (el && el !== e.currentTarget) {
      if (el.scrollWidth > el.clientWidth + 4) { pannable = true; break; }
      el = el.parentElement;
    }
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now(), pannable };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const s = touchStart.current;
    touchStart.current = null;
    if (!s || s.pannable || Date.now() - s.t > 600) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < 60 || Math.abs(dx) < 2 * Math.abs(dy)) return;
    if (dx < 0) goTo(current + 1, 'right'); // finger left → NEXT note enters from the right
    else goTo(current - 1, 'left');          // finger right → PREV note enters from the left
  };

  if (!notes || notes.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: 'white' }}>
        <Typography color="text.secondary">No notes or documents.</Typography>
      </Paper>
    );
  }

  const safeCurrent = Math.min(current, Math.max(0, visible.length - 1));
  const note = visible[safeCurrent];
  const atEnd = safeCurrent >= visible.length - 1 && !hasMore;
  // Counter denominator ≈ server total minus the boilerplate rows skipped so far.
  const denom = visible.length + (total - notes.length);
  const showNav = visible.length > 1 || hasMore;

  const arrowSx = {
    position: 'absolute' as const, top: `${navPos.top}px`, transform: 'translateY(-50%)', pointerEvents: 'auto' as const,
    width: { xs: 64, sm: 88 }, height: { xs: 88, sm: 112 },
    bgcolor: 'transparent', color: '#aeaeb2',
    '&:hover': { bgcolor: 'transparent', color: '#6b6b70' },
    '&.Mui-disabled': { opacity: 0.3 },
  };

  return (
    <Box onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {note && (
        <Box
          key={safeCurrent}
          ref={noteWrapRef}
          sx={{
            scrollMarginTop: `${READING_LINE}px`,
            ...(slideDir === 'right' ? { animation: 'hwNoteSlideR 250ms ease-out' }
              : slideDir === 'left' ? { animation: 'hwNoteSlideL 250ms ease-out' } : {}),
            '@keyframes hwNoteSlideR': { from: { opacity: 0, transform: 'translateX(42px)' }, to: { opacity: 1, transform: 'none' } },
            '@keyframes hwNoteSlideL': { from: { opacity: 0, transform: 'translateX(-42px)' }, to: { opacity: 1, transform: 'none' } },
          }}
        >
          <NoteBlock note={note} />
        </Box>
      )}

      {/* Navigator: big ghost < > sitting ON the note's edges (overlay constrained to the note COLUMN width,
          not the screen) AND vertically LINKED to the note — centered on its visible slice, hidden when the
          note is off-screen (navPos.visible) so they never float over the pills. Counter pill bottom-center. */}
      {showNav && navPos.visible && (
        <>
          <Box sx={{ position: 'fixed', inset: 0, zIndex: 1200, pointerEvents: 'none' }}>
            <Box sx={{ position: 'relative', height: '100%', width: { xs: '100%', sm: '94%' }, mx: 'auto' }}>
              <IconButton aria-label="Previous note" disabled={safeCurrent === 0} onClick={() => goTo(safeCurrent - 1)} sx={{ ...arrowSx, left: 0 }}>
                <ThinChevron dir="left" />
              </IconButton>
              <IconButton aria-label="Next note" disabled={atEnd} onClick={() => goTo(safeCurrent + 1)} sx={{ ...arrowSx, right: 0 }}>
                {loadingMore && pendingIdx.current !== null
                  ? <CircularProgress size={26} sx={{ color: '#aeaeb2' }} />
                  : <ThinChevron dir="right" />}
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1200 }}>
            <Paper elevation={0} sx={{ px: 1.5, py: 0.5, borderRadius: 50, bgcolor: 'white', border: '1px solid #e2e2e8', boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }}>
              <Typography sx={{ fontSize: 12, color: '#6b6b70', whiteSpace: 'nowrap' }}>
                {safeCurrent + 1} of {denom}
              </Typography>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};
